/**
 * Attachment cards and their preview (prototype `attCard` / `previewAtt`).
 *
 * The card's second button is the point of the whole screen: an xlsx, pdf or image can be
 * turned straight into a requirement. Anything else gets preview only — offering "Start RFQ"
 * on a .docx just earns a 422.
 *
 * `.xlsx` gets one more thing: an in-app spreadsheet render (`XlsxAttachmentPreview` below),
 * because the one attachment this screen sees a lot of is the RFQ workbook the backend already
 * generates (`docs/bosun-frappe-open-questions.md` Q12) — a purchaser reading a supplier's
 * filled-in reply, or checking what was sent, shouldn't have to leave the app to do it, and a
 * cramped plain table doesn't read like the spreadsheet it actually is.
 *
 * `.pdf` gets a native render (`PdfAttachmentPreview` below) — the browser's own PDF engine
 * against the fetched bytes, for real fidelity, with an extracted-text fallback (`unpdf`, the
 * same library `useMailExtraction.ts` already uses) when the native view doesn't load.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Modal } from '../../components/Modal'
import { Button, Placeholder } from '../../components/ui'
import { ATTACHMENT_ICON } from '../../lib/mail'
import { columnLetter, fetchAndParseXlsxPreview } from '../../lib/xlsxPreview'
import { FileImage, FileLink } from '../../components/FileLink'
import type { XlsxImage, XlsxPreviewResult, XlsxSheetPreview } from '../../lib/xlsxPreview'
import { extractPdfText, fetchPdfBytes } from '../../lib/pdfPreview'
import { useCachedAttachmentLoader } from '../../lib/api/attachmentBlob'
import { prewarmExtraction } from '../../lib/api/useMailExtraction'
import { useSocketStatus } from '../../lib/api/useSocketStatus'
import { shouldPrewarm } from '../../lib/mailPrewarm'
import type { MailAttachment, MailMessage } from '../../lib/types'

/**
 * The per-attachment card. Preview only.
 *
 * A "⚙ RFQ" button used to sit beside Preview, turning one file straight into a requirement. It
 * is gone: per-attachment extraction never existed (Q8), and creating an enquiry now happens in
 * `CreateEnquiryBar` below the mail body, where one AI read covers every attachment at once. The
 * `busy` / `onStartRfq` props went with it.
 */
export function AttachmentCard({
  attachment,
  onPreview,
}: {
  attachment: MailAttachment
  onPreview: () => void
}) {

  return (
    <div className="attcard">
      <button type="button" className="thumb" onClick={onPreview} aria-label={`Preview ${attachment.name}`}>
        {attachment.kind === 'img' && attachment.preview_url ? (
          <FileImage src={attachment.preview_url} alt={attachment.name} />
        ) : (
          ATTACHMENT_ICON[attachment.kind]
        )}
      </button>
      <div className="meta">
        <div className="fn" title={attachment.name}>
          {attachment.name}
        </div>
        <div className="fs">{attachment.size}</div>
        <div style={{ display: 'flex', gap: 5, marginTop: 6 }}>
          <Button size="sm" onClick={onPreview}>
            👁 Preview
          </Button>
        </div>
      </div>
    </div>
  )
}

/** How wide the modal goes for kinds that actually need the room. `Modal`'s `.box` already caps
 * at `max-width: 92vw`, so passing a generous px width here stays responsive on a laptop
 * screen on its own — no extra clamping needed. */
const PREVIEW_MODAL_WIDTH: Partial<Record<MailAttachment['kind'], number>> = {
  xlsx: 1180,
  pdf: 880,
}

/**
 * Full preview of one attachment. Preview only — the "Start RFQ from this file" footer action was
 * removed for the same reason as `AttachmentCard`'s: per-file extraction does not exist (Q8), and
 * enquiries are now created in `CreateEnquiryBar` under the mail body, where one AI read covers
 * every attachment together.
 */
export function AttachmentPreviewModal({
  attachment,
  mail,
  onClose,
  prewarm = false,
}: {
  attachment: MailAttachment
  /** The mail this attachment belongs to — needed only to decide whether to pre-warm. */
  mail: MailMessage
  onClose: () => void
  /**
   * Opt in to firing `prewarmExtraction` when this modal opens. Defaults to `false` because this
   * modal is shared: the Mail screen (`MailPage.tsx`) is the one place opening an attachment is a
   * genuine "does this look like a new enquiry?" signal worth an LLM call. `SourcePanel.tsx` (the
   * `/rfq` source dock) renders the SAME modal for an enquiry that already exists and whose lines
   * are already extracted — pre-warming there would fire an LLM call and download+parse the
   * workbook for a result nothing on that screen reads, on every open, because the source mail on
   * `/rfq` is by construction an RFQ mail and always clears `RFQ_THRESHOLD`. Leave it unset (or
   * explicitly `false`) at every call site except Mail's.
   */
  prewarm?: boolean
}) {
  // Pre-warm: opening an attachment is the strongest intent signal short of clicking "Create
  // enquiry", so this is where a background extraction is worth an LLM call. Silent by design —
  // `prewarmExtraction` surfaces nothing, and `startRun` makes a second open a no-op. Gated by
  // `prewarm` — see the prop's own doc comment for why this must not be unconditional.
  const socketStatus = useSocketStatus()
  const prewarmClient = useQueryClient()
  const prewarmLoadBlob = useCachedAttachmentLoader()
  useEffect(() => {
    if (!prewarm) return
    if (!shouldPrewarm(mail)) return
    prewarmExtraction(mail, {
      queryClient: prewarmClient,
      loadBlob: prewarmLoadBlob,
    })
  }, [prewarm, mail, socketStatus, prewarmClient, prewarmLoadBlob])

  return (
    <Modal
      title={
        <span>
          📎 {attachment.name}{' '}
          <span style={{ color: 'var(--muted)', fontWeight: 500 }}>· {attachment.size}</span>
        </span>
      }
      onClose={onClose}
      width={PREVIEW_MODAL_WIDTH[attachment.kind]}
      footer={
        <>
          <span style={{ flex: 1 }} />
          <Button onClick={onClose}>Close</Button>
        </>
      }
    >
      <div className="mb">
        <AttachmentPreviewBody attachment={attachment} />
      </div>
    </Modal>
  )
}

/** A real download, when the attachment has anywhere to download it from. */
function DownloadLink({ attachment }: { attachment: MailAttachment }) {
  return (
    <FileLink href={attachment.preview_url} name={attachment.name}>
      ⬇ Download {attachment.name}
    </FileLink>
  )
}

/** The shared "couldn't preview this file" panel both the xlsx and pdf branches fall back to —
 * same look, same honesty: a reason and a real download link, never a blank pane pretending to
 * be a preview. */
function PreviewFailure({ detail, attachment }: { detail: string; attachment: MailAttachment }) {
  return (
    <div style={{ padding: 18 }}>
      <div
        style={{
          background: 'var(--dangerbg)',
          border: '1px solid var(--dangerline)',
          borderRadius: 8,
          padding: 16,
        }}
      >
        <div style={{ fontWeight: 700, marginBottom: 6, color: 'var(--danger)' }}>Couldn't preview this file</div>
        <div style={{ fontSize: 12.5, color: 'var(--muted)', marginBottom: 10 }}>{detail}</div>
        <DownloadLink attachment={attachment} />
      </div>
    </div>
  )
}

/* ============================================================================================
 * .xlsx — a real spreadsheet grid: column letters + row numbers as chrome, both frozen, sheet
 * tabs when there's more than one, numbers right-aligned with tabular figures.
 * ==========================================================================================*/

type XlsxPreviewState = { status: 'loading' } | { status: 'done'; result: XlsxPreviewResult }

/** Fetches and parses `url` on mount / whenever it changes; never lets a rejection escape. */
function useXlsxPreview(url: string | null | undefined): XlsxPreviewState {
  const [state, setState] = useState<XlsxPreviewState>({ status: 'loading' })
  // Shared with the AI-extraction fallback (`useMailExtraction.ts`) — previewing a sheet and
  // then reading the mail costs ONE download, not two. See `api/attachmentBlob.ts`.
  const loadBlob = useCachedAttachmentLoader()

  useEffect(() => {
    let cancelled = false
    setState({ status: 'loading' })

    if (!url) {
      setState({
        status: 'done',
        result: { ok: false, reason: 'fetch_failed', detail: 'No file location was returned for this attachment.' },
      })
      return
    }

    void (async () => {
      try {
        const result = await fetchAndParseXlsxPreview(url, loadBlob)
        if (!cancelled) setState({ status: 'done', result })
      } catch (error) {
        // `fetchAndParseXlsxPreview` already turns every known failure into a value — this
        // only catches a genuinely unexpected throw, so the modal shows a message instead of
        // an unhandled rejection.
        if (!cancelled) {
          setState({
            status: 'done',
            result: {
              ok: false,
              reason: 'parse_failed',
              detail: error instanceof Error ? error.message : 'This spreadsheet could not be read.',
            },
          })
        }
      }
    })()

    return () => {
      cancelled = true
    }
  }, [url, loadBlob])

  return state
}

/** One `blob:` URL per embedded image (created for EVERY image, including formats the browser
 * can't render — an unsupported one still needs a URL to be individually downloadable), rebuilt
 * whenever the image list changes and revoked on the way out. This is the only place in the
 * xlsx preview that creates object URLs, so there is exactly one cleanup path to keep correct —
 * important in a long-lived SPA where a purchaser can open many attachments in a session without
 * a page reload ever coming along to reclaim them. */
function useImageObjectUrls(images: XlsxImage[]): Map<string, string> {
  const [urls, setUrls] = useState<Map<string, string>>(new Map())

  useEffect(() => {
    const next = new Map<string, string>()
    for (const image of images) {
      next.set(image.path, URL.createObjectURL(new Blob([image.bytes], { type: image.mime ?? 'application/octet-stream' })))
    }
    setUrls(next)
    return () => {
      next.forEach((url) => URL.revokeObjectURL(url))
    }
    // `images` is a fresh array reference only when the underlying preview actually changes
    // (`XlsxAttachmentPreview` builds it with `useMemo`, keyed on that same preview) — safe as
    // the effect dependency without re-creating URLs on every unrelated re-render.
  }, [images])

  return urls
}

/** The `.xlsx` branch of the attachment preview: fetches the file, parses every sheet
 * (`lib/xlsxPreview.ts`), and renders each as a real spreadsheet grid — column letters and row
 * numbers as chrome, both frozen, numbers right-aligned — plus sheet tabs, a row/column count,
 * an explicit truncation note when one applies, and a download link either way.
 *
 * Embedded images (photos a customer drops into their own requisition workbook — a nameplate, a
 * part — see `lib/xlsxImages.ts`) are shown per sheet, directly below that sheet's grid: real
 * requisition workbooks put every photo on a dedicated `photos-docs` sheet with no cell data at
 * all, which `xlsxPreview.ts` now keeps in `sheets` specifically so this has somewhere to render
 * them. Images this parser couldn't tie to a sheet fall into one labelled `otherImages` gallery
 * instead of being dropped, and a scan failure (couldn't even list the images) is a visible note,
 * never silence — see this module's header and `xlsxPreview.ts`'s for the full reasoning.
 */
function XlsxAttachmentPreview({ attachment }: { attachment: MailAttachment }) {
  const state = useXlsxPreview(attachment.preview_url)
  const [sheetIndex, setSheetIndex] = useState(0)
  const [openImage, setOpenImage] = useState<XlsxImage | null>(null)

  // A new attachment (or a re-fetch of the same one) always starts back on its first sheet, with
  // no lightbox left open from whatever was on screen before.
  useEffect(() => {
    setSheetIndex(0)
    setOpenImage(null)
  }, [attachment.preview_url])

  const preview = state.status === 'done' && state.result.ok ? state.result.preview : null

  // Every image in the workbook, sheet-attached or not — one flat list so `useImageObjectUrls`
  // only has to manage a single map, regardless of which sheet tab or gallery a thumbnail
  // actually renders in.
  const allImages = useMemo(
    () => (preview ? [...preview.sheets.flatMap((sheet) => sheet.images), ...preview.otherImages] : []),
    [preview],
  )
  const imageUrls = useImageObjectUrls(allImages)

  if (state.status === 'loading') {
    return (
      <div style={{ padding: 18 }}>
        <Placeholder>Loading preview…</Placeholder>
      </div>
    )
  }

  if (!state.result.ok) {
    return <PreviewFailure detail={state.result.detail} attachment={attachment} />
  }

  const { sheets, otherImages, imageScanError } = state.result.preview
  const hasSheets = sheets.length > 0
  const activeIndex = hasSheets ? Math.min(sheetIndex, sheets.length - 1) : -1
  const sheet = hasSheets ? sheets[activeIndex] : null
  const sheetHasTable = sheet !== null && (sheet.columns.length > 0 || sheet.rows.length > 0)

  return (
    <div style={{ padding: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <div className="xlsheet-meta">
          {sheet ? (
            <>
              <span>
                {sheet.rowCount.toLocaleString()} row{sheet.rowCount === 1 ? '' : 's'} × {sheet.columnCount} column
                {sheet.columnCount === 1 ? '' : 's'}
              </span>
              {sheet.truncated ? (
                <span className="truncated">
                  showing the first {sheet.rows.length.toLocaleString()} rows only — truncated
                </span>
              ) : null}
            </>
          ) : (
            <span>No tabular data on any sheet.</span>
          )}
        </div>
        <DownloadLink attachment={attachment} />
      </div>

      {imageScanError ? (
        <div className="xlimg-scan-error">
          This workbook's embedded images could not be scanned ({imageScanError}) — <DownloadLink attachment={attachment} /> to
          view the file directly.
        </div>
      ) : null}

      {sheet?.headerLines.length ? (
        <div className="xlsheet-doc-header">
          {sheet.headerLines.map((line, i) => (
            <div key={i}>{line}</div>
          ))}
        </div>
      ) : null}

      {sheet ? (
        <>
          {sheetHasTable ? (
            <SheetGrid sheet={sheet} />
          ) : sheet.images.length === 0 ? (
            <Placeholder>This sheet has no tabular data.</Placeholder>
          ) : (
            <div className="xlsheet-images-only-note">This sheet has no tabular data — just the images below.</div>
          )}
          {sheet.images.length > 0 ? (
            <ImageGallery
              title={`Images on this sheet (${sheet.images.length})`}
              images={sheet.images}
              urls={imageUrls}
              onOpen={setOpenImage}
            />
          ) : null}
        </>
      ) : (
        <Placeholder>This workbook has no tabular data.</Placeholder>
      )}

      {sheets.length > 1 ? (
        <div className="xlsheet-tabs" role="tablist" aria-label="Worksheet">
          {sheets.map((s, i) => (
            <button
              key={`${s.name}-${i}`}
              type="button"
              role="tab"
              aria-selected={i === activeIndex}
              className={`xlsheet-tab${i === activeIndex ? ' active' : ''}`}
              onClick={() => setSheetIndex(i)}
            >
              {s.name}
              {s.images.length > 0 ? ` (${s.images.length} img)` : ''}
            </button>
          ))}
        </div>
      ) : null}

      {otherImages.length > 0 ? (
        <ImageGallery
          title={`Other images (${otherImages.length})`}
          note="Couldn't be matched to a specific sheet."
          images={otherImages}
          urls={imageUrls}
          onOpen={setOpenImage}
        />
      ) : null}

      {openImage ? (
        <ImageLightbox image={openImage} url={imageUrls.get(openImage.path) ?? null} onClose={() => setOpenImage(null)} />
      ) : null}
    </div>
  )
}

/**
 * A labelled thumbnail gallery for one group of embedded images. A renderable format (png/jpeg/
 * gif/bmp/webp) becomes a clickable thumbnail that opens `ImageLightbox` at full size; a format
 * no browser renders (emf/wmf and anything else `xlsxImages.ts` doesn't recognise) becomes its
 * own clearly-labelled, still-downloadable tile — never a broken `<img>` standing in for it.
 */
function ImageGallery({
  title,
  note,
  images,
  urls,
  onOpen,
}: {
  title: string
  note?: string
  images: XlsxImage[]
  urls: Map<string, string>
  onOpen: (image: XlsxImage) => void
}) {
  const unsupportedCount = images.filter((image) => image.mime === null).length

  return (
    <div className="xlimg-section">
      <div className="xlimg-section-title">
        <span>{title}</span>
        {note ? <span className="xlimg-section-note">{note}</span> : null}
        {unsupportedCount > 0 ? (
          <span className="xlimg-section-note">
            {unsupportedCount} of {images.length} {unsupportedCount === 1 ? "isn't" : "aren't"} previewable in-browser —
            download to view
          </span>
        ) : null}
      </div>
      <div className="xlimg-grid">
        {images.map((image) => {
          const url = urls.get(image.path)
          if (!image.mime) {
            return (
              <a
                key={image.path}
                className="xlimg-thumb unsupported"
                href={url}
                download={image.name}
                title={`${image.name} — ${(image.format || 'this format').toUpperCase()} can't be previewed here; click to download`}
              >
                <span className="xlimg-badge">{(image.format || 'file').toUpperCase()}</span>
                <span>Can't preview — download</span>
              </a>
            )
          }
          return (
            <button
              key={image.path}
              type="button"
              className="xlimg-thumb"
              onClick={() => onOpen(image)}
              aria-label={`View ${image.name} full size`}
              title={image.name}
            >
              {url ? <img src={url} alt={image.name} loading="lazy" /> : null}
            </button>
          )
        })}
      </div>
    </div>
  )
}

/** Full-size view of one embedded image — the same `Modal` idiom (and the same
 * padding/border/radius) already used for an `img`-kind mail attachment above, nested on top of
 * the xlsx preview modal so a purchaser can read a nameplate without losing their place in the
 * sheet. */
function ImageLightbox({ image, url, onClose }: { image: XlsxImage; url: string | null; onClose: () => void }) {
  return (
    <Modal
      title={image.name}
      onClose={onClose}
      footer={
        <>
          <span style={{ flex: 1 }} />
          {url ? (
            <a className="linkish" href={url} download={image.name}>
              ⬇ Download
            </a>
          ) : null}
          <Button onClick={onClose}>Close</Button>
        </>
      }
    >
      <div style={{ padding: 16, textAlign: 'center' }}>
        {url ? (
          <img
            src={url}
            alt={image.name}
            style={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: 8, border: '1px solid var(--line)' }}
          />
        ) : (
          <Placeholder>This image could not be loaded.</Placeholder>
        )}
      </div>
    </Modal>
  )
}

/**
 * The grid itself. Two frozen header rows (column letters, then the sheet's real column-title
 * row directly beneath — `--xlrow-h`/`--xltitle-h` in `global.css` keep their `top` offsets in
 * sync) and a frozen row-number gutter down the left, so the corner where both axes meet stays
 * put no matter which way the sheet scrolls. When a sheet has no title row (no dense row was
 * found — see `buildSheetPreview`), the title row is skipped and every row is numbered from 1.
 *
 * Column letters add `sheet.colOffset` before formatting — trimmed leading columns (see
 * `xlsxPreview.ts`'s `trimBlankEdges`) must not renumber the remaining ones from `A`, or the
 * letters stop matching what this same file shows in Excel. The `<colgroup>` applies
 * `sheet.columnWidthsPx` as a per-column sizing hint (browser auto-layout can still widen a
 * column past it for genuinely long content, capped by the `max-width` on `td`/`th` below) —
 * `undefined` entries are left to auto-layout entirely. A data cell with `covered: true` (inside
 * a merged region but not its anchor — see `XlsxCell.covered`) renders no `<td>` at all; the
 * anchor's own `colSpan`/`rowSpan` is what actually reserves that space, exactly as HTML tables
 * expect.
 */
function SheetGrid({ sheet }: { sheet: XlsxSheetPreview }) {
  const hasTitleRow = sheet.columns.length > 0
  const colIndexes = Array.from({ length: sheet.columnCount }, (_, i) => i)

  return (
    <div className="xlsheet-wrap">
      <table className="xlsheet">
        <colgroup>
          <col />
          {colIndexes.map((c) => {
            const width = sheet.columnWidthsPx[c]
            return <col key={c} style={width ? { width } : undefined} />
          })}
        </colgroup>
        <thead>
          <tr className="xl-letters">
            <th className="xl-rownum" aria-hidden="true" />
            {colIndexes.map((c) => (
              <th key={c}>{columnLetter(c + sheet.colOffset)}</th>
            ))}
          </tr>
          {hasTitleRow ? (
            <tr className="xl-titles">
              <th className="xl-rownum">1</th>
              {colIndexes.map((c) => {
                const title = sheet.columns[c] ?? ''
                return (
                  <th key={c} title={title || undefined}>
                    {title}
                  </th>
                )
              })}
            </tr>
          ) : null}
        </thead>
        <tbody>
          {sheet.rows.map((row, ri) => (
            <tr key={ri}>
              <td className="xl-rownum">{ri + (hasTitleRow ? 2 : 1)}</td>
              {colIndexes.map((c) => {
                const cell = row[c]
                if (cell?.covered) return null
                return (
                  <td
                    key={c}
                    className={cell?.numeric ? 'numeric' : undefined}
                    colSpan={cell?.colSpan}
                    rowSpan={cell?.rowSpan}
                    title={cell?.text || undefined}
                  >
                    {cell?.text ?? ''}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/* ============================================================================================
 * .pdf — a native render against the fetched bytes, with an extracted-text fallback.
 * ==========================================================================================*/

/** How long the native `<iframe>` gets to fire `onLoad` before this treats it as blocked and
 * switches to the extracted-text fallback. A `blob:` URL of a real PDF loads in a browser's
 * built-in viewer near-instantly, so a multi-second miss is a genuine signal something is
 * wrong (no PDF viewer available, a restrictive CSP/webview), not just a slow render. */
const PDF_NATIVE_LOAD_TIMEOUT_MS = 4000

type PdfBytesState =
  | { status: 'loading' }
  | { status: 'ready'; bytes: Uint8Array; blobUrl: string }
  | { status: 'failed'; detail: string }

/** Fetches the PDF's bytes once per `url` and hands back a `blob:` URL for the `<iframe>` plus
 * the raw bytes for the text-extraction fallback — one fetch serves both. Revokes the object
 * URL on cleanup so a purchaser opening several PDFs in a session doesn't leak them. */
function usePdfBytes(url: string | null | undefined): PdfBytesState {
  const [state, setState] = useState<PdfBytesState>({ status: 'loading' })
  // Same shared download as the xlsx path above.
  const loadBlob = useCachedAttachmentLoader()

  useEffect(() => {
    let cancelled = false
    let ownedBlobUrl: string | undefined
    setState({ status: 'loading' })

    if (!url) {
      setState({ status: 'failed', detail: 'No file location was returned for this attachment.' })
      return
    }

    void (async () => {
      const result = await fetchPdfBytes(url, loadBlob)
      if (cancelled) {
        if (result.ok) URL.revokeObjectURL(result.blobUrl)
        return
      }
      if (!result.ok) {
        setState({ status: 'failed', detail: result.detail })
        return
      }
      ownedBlobUrl = result.blobUrl
      setState({ status: 'ready', bytes: result.bytes, blobUrl: result.blobUrl })
    })()

    return () => {
      cancelled = true
      if (ownedBlobUrl) URL.revokeObjectURL(ownedBlobUrl)
    }
  }, [url, loadBlob])

  return state
}

type PdfViewMode = 'native' | 'text'
type PdfTextState = { status: 'idle' } | { status: 'loading' } | { status: 'ready'; text: string } | { status: 'unavailable' }

/**
 * The `.pdf` branch: a native `<iframe>` against the fetched bytes for real fidelity, an
 * automatic switch to extracted text (`unpdf`, same call `useMailExtraction.ts` makes) if the
 * native view hasn't loaded within `PDF_NATIVE_LOAD_TIMEOUT_MS`, a manual toggle either
 * direction, and an explicit failure message — never a blank frame — if neither works.
 */
function PdfAttachmentPreview({ attachment }: { attachment: MailAttachment }) {
  const pdfState = usePdfBytes(attachment.preview_url)
  const [mode, setMode] = useState<PdfViewMode>('native')
  const [nativeLoaded, setNativeLoaded] = useState(false)
  const [nativeTimedOut, setNativeTimedOut] = useState(false)
  const [textState, setTextState] = useState<PdfTextState>({ status: 'idle' })

  useEffect(() => {
    setMode('native')
    setNativeLoaded(false)
    setNativeTimedOut(false)
    setTextState({ status: 'idle' })
  }, [attachment.preview_url])

  // Give the native viewer a window to prove itself once bytes are in hand.
  useEffect(() => {
    if (pdfState.status !== 'ready' || nativeLoaded) return
    const timer = setTimeout(() => setNativeTimedOut(true), PDF_NATIVE_LOAD_TIMEOUT_MS)
    return () => clearTimeout(timer)
  }, [pdfState.status, nativeLoaded])

  const runExtraction = useCallback(() => {
    if (pdfState.status !== 'ready') return
    setTextState({ status: 'loading' })
    void extractPdfText(pdfState.bytes).then((text) => {
      setTextState(text.trim() !== '' ? { status: 'ready', text } : { status: 'unavailable' })
    })
  }, [pdfState])

  // The native view timed out and nothing has tried extraction yet: switch over automatically
  // rather than leaving a purchaser staring at a blank frame.
  useEffect(() => {
    if (nativeTimedOut && !nativeLoaded && textState.status === 'idle') {
      setMode('text')
      runExtraction()
    }
  }, [nativeTimedOut, nativeLoaded, textState.status, runExtraction])

  const handleModeChange = (next: PdfViewMode) => {
    setMode(next)
    if (next === 'text' && textState.status === 'idle') runExtraction()
  }

  if (pdfState.status === 'loading') {
    return (
      <div style={{ padding: 18 }}>
        <Placeholder>Loading preview…</Placeholder>
      </div>
    )
  }

  if (pdfState.status === 'failed') {
    return <PreviewFailure detail={pdfState.detail} attachment={attachment} />
  }

  return (
    <div style={{ padding: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 10 }}>
        <div style={{ display: 'flex', gap: 6 }}>
          <Button size="sm" variant={mode === 'native' ? 'primary' : 'default'} onClick={() => handleModeChange('native')}>
            📄 Native viewer
          </Button>
          <Button size="sm" variant={mode === 'text' ? 'primary' : 'default'} onClick={() => handleModeChange('text')}>
            📝 Extracted text
          </Button>
        </div>
        <DownloadLink attachment={attachment} />
      </div>

      {mode === 'native' ? (
        <div>
          <div style={{ fontSize: 11.5, color: 'var(--muted)', marginBottom: 6 }}>
            Native PDF viewer{!nativeLoaded && !nativeTimedOut ? ' — loading…' : ''}
          </div>
          <div style={{ border: '1px solid var(--line)', borderRadius: 8, overflow: 'hidden', height: '62vh' }}>
            <iframe
              src={pdfState.blobUrl}
              title={attachment.name}
              onLoad={() => setNativeLoaded(true)}
              style={{ width: '100%', height: '100%', border: 'none', background: 'var(--panel)' }}
            />
          </div>
        </div>
      ) : (
        <div>
          <div style={{ fontSize: 11.5, color: 'var(--muted)', marginBottom: 6 }}>
            Extracted text{nativeTimedOut && !nativeLoaded ? " — the native viewer didn't load in time" : ''}
          </div>
          {textState.status === 'loading' ? (
            <Placeholder>Extracting text…</Placeholder>
          ) : textState.status === 'ready' ? (
            <pre
              style={{
                margin: 0,
                padding: 14,
                border: '1px solid var(--line)',
                borderRadius: 8,
                background: 'var(--bg)',
                color: 'var(--ink)',
                maxHeight: '58vh',
                overflow: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontFamily: 'inherit',
                fontSize: 12.5,
                lineHeight: 1.6,
              }}
            >
              {textState.text}
            </pre>
          ) : (
            <PreviewFailure
              detail="Neither the native viewer nor text extraction could read this file — it may be a scanned/image-only PDF."
              attachment={attachment}
            />
          )}
        </div>
      )}
    </div>
  )
}

/**
 * The preview itself, without the dialog — so the RFQ builder's source dock can show an
 * attachment IN the panel beside the lines (Akhil, 2026-09-18: "better to have it in the side
 * panel like the mail, so the user can see the list of items") instead of a box in the middle.
 */
export function AttachmentPreviewBody({ attachment }: { attachment: MailAttachment }) {
  return (
    <>
    {attachment.kind === 'img' && attachment.preview_url ? (
      <div style={{ padding: 16, textAlign: 'center' }}>
        <FileImage
          src={attachment.preview_url}
          alt={attachment.name}
          style={{ maxWidth: '100%', borderRadius: 8, border: '1px solid var(--line)' }}
        />
      </div>
    ) : attachment.kind === 'xlsx' ? (
      <XlsxAttachmentPreview attachment={attachment} />
    ) : attachment.kind === 'pdf' ? (
      <PdfAttachmentPreview attachment={attachment} />
    ) : (
      /* doc / file / an img with no url: rendering the document is the backend's job.
         Showing a believable placeholder beats faking file contents we don't have — a
         download link is the honest version of "preview" here. */
      <div style={{ padding: 18 }}>
        <div
          style={{
            background: 'var(--panel)',
            border: '1px solid var(--line)',
            borderRadius: 8,
            padding: 22,
            minHeight: 180,
            boxShadow: 'var(--shadow)',
          }}
        >
          <div style={{ fontSize: 34, marginBottom: 10 }}>{ATTACHMENT_ICON[attachment.kind]}</div>
          <div style={{ fontWeight: 800, marginBottom: 6 }}>{attachment.name}</div>
          <div style={{ color: 'var(--muted)', fontSize: 12.5 }}>
            {attachment.preview_url ? (
              <>
                Rendered preview comes from the server.{' '}
                <FileLink href={attachment.preview_url} name={attachment.name}>
                  Open the file
                </FileLink>
                .
              </>
            ) : (
              'No preview available for this file.'
            )}
          </div>
        </div>
      </div>
    )}
    </>
  )
}
